/**
 * The demo generator's configuration: tiers, platform modifiers and the date
 * anchor everything else is measured from. The bottom of the DAG — it imports
 * only funnelConfig, and every other demo module imports it.
 */

import { LEAD_GEN_FUNNEL, type FunnelConfig } from '@/config/funnelConfig';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Demo user credentials
 */
export interface DemoUserCredentials {
  email: string
  password: string
  displayName: string
}

/**
 * Performance tier configuration for scenarios
 */
export interface PerformanceTierConfig {
  roasRange: [number, number]
  ctrRange: [number, number]
  cplRange: [number, number]
  trendDirection: 'up' | 'flat' | 'down'
  trendStrength: number
}

/**
 * Platform distribution for a scenario
 */
export interface PlatformDistribution {
  facebook: number // percentage of spend
  google_display: number
  google_search: number
  performance_max: number
  tiktok: number
  line: number
}

/**
 * KPI expectations for a scenario
 */
export interface KPIExpectations {
  targetROAS: number
  targetCTR: number
  targetCPL: number
  conversionRate: number
}

/**
 * Demo configuration
 */
export interface DemoConfig {
  name: string
  industry: string
  workspaceId: string
  demoUser: DemoUserCredentials
  funnel: FunnelConfig
  performanceProfiles: Record<string, PerformanceTierConfig>
  platformMix: PlatformDistribution
  kpiExpectations: KPIExpectations
  totalMonthlyBudget: number
}

// ============================================================================
// DEMO DATE ANCHOR - Martech Expo 2026
// ============================================================================

/**
 * Demo date anchor - all demo data appears as if on this date
 * Set to March 24, 2026 for Martech Expo 2026
 */
export const DEMO_DATE_ANCHOR = '2026-03-24'

/**
 * Demo data range - 90 days of Q1 data
 * From December 25, 2025 to March 24, 2026
 */
export const DEMO_DATA_RANGE_DAYS = 90

// ============================================================================
// CONSTANTS
// ============================================================================

// Platform series colors use the YDM IDEAS secondary palette (Orange/Green/
// Blue/Cyan/Pink) — each channel becomes one of the I-D-E-A-S accent colors so
// charts read as "this is YDM". LINE keeps its own brand green because the
// platform identity is iconic and a 6th channel falls outside the 5-letter
// IDEAS set.
export const PLATFORMS = {
  facebook: {
    name: 'Facebook',
    icon: 'facebook',
    color: '#F7941D', // IDEAS orange (I)
    spendPercentage: 0.4,
    targetROAS: 3.8,
    funnelFocus: 'awareness' as const,
  },
  google_display: {
    name: 'Google Display',
    icon: 'layout',
    color: '#4667FF', // IDEAS blue (E)
    spendPercentage: 0.15,
    targetROAS: 2.5,
    funnelFocus: 'awareness' as const,
  },
  google_search: {
    name: 'Google Search',
    icon: 'search',
    color: '#00BEF9', // IDEAS cyan (A)
    spendPercentage: 0.15,
    targetROAS: 4.5,
    funnelFocus: 'consideration' as const,
  },
  performance_max: {
    name: 'Performance Max',
    icon: 'zap',
    color: '#94F53A', // IDEAS green (D)
    spendPercentage: 0.1,
    targetROAS: 5.0,
    funnelFocus: 'conversion' as const,
  },
  tiktok: {
    name: 'TikTok',
    icon: 'video',
    color: '#FE4FFF', // IDEAS pink (S)
    spendPercentage: 0.15,
    targetROAS: 3.1,
    funnelFocus: 'awareness' as const,
  },
  line: {
    name: 'LINE Ads',
    icon: 'message-circle',
    color: '#00B900', // LINE brand green — kept for platform recognition
    spendPercentage: 0.1,
    targetROAS: 3.5,
    funnelFocus: 'consideration' as const,
  },
} as const

export type PlatformKey = keyof typeof PLATFORMS

// ============================================================================
// DEMO SCENARIO CONFIGURATIONS
// ============================================================================

/**
 * Demo configuration
 * Focus: Lead Generation - steady pipeline, higher CPL
 */
export const DEMO_CONFIG: DemoConfig = {
  name: 'Lead Generation Demo',
  industry: 'lead_gen',
  workspaceId: '550e8400-e29b-41d4-a716-446655440002',
  demoUser: {
    email: 'leadgen@demo.local',
    password: 'demo1234',
    displayName: 'Lead Gen Demo User',
  },
  funnel: LEAD_GEN_FUNNEL,
  performanceProfiles: {
    tier1: {
      roasRange: [3.0, 4.5],
      ctrRange: [2.5, 4.0],
      cplRange: [300, 450],
      trendDirection: 'up',
      trendStrength: 0.6,
    },
    tier2: {
      roasRange: [2.2, 3.0],
      ctrRange: [2.0, 2.5],
      cplRange: [450, 550],
      trendDirection: 'up',
      trendStrength: 0.2,
    },
    tier3: {
      roasRange: [1.5, 2.2],
      ctrRange: [1.2, 2.0],
      cplRange: [550, 650],
      trendDirection: 'flat',
      trendStrength: 0.0,
    },
    tier4: {
      roasRange: [0.8, 1.5],
      ctrRange: [0.6, 1.2],
      cplRange: [650, 700],
      trendDirection: 'down',
      trendStrength: -0.2,
    },
  },
  platformMix: {
    facebook: 0.3,
    google_display: 0.1,
    google_search: 0.35,
    performance_max: 0.15,
    tiktok: 0.12,
    line: 0.1,
  },
  kpiExpectations: {
    targetROAS: 2.8,
    targetCTR: 2.2,
    targetCPL: 280,
    conversionRate: 3.2,
  },
  totalMonthlyBudget: 950000,
}

// ============================================================================
// PERFORMANCE TIER SYSTEM
// ============================================================================

/**
 * Performance tier configuration for realistic campaign variation
 * Each tier defines ROAS, CTR, trend direction, and variance characteristics
 */
export const PERFORMANCE_TIERS = {
  tier1: {
    // High performers (30%)
    roasRange: [3.5, 5.0],
    ctrRange: [3.0, 5.0],
    trendDirection: 'up' as const,
    trendStrength: 0.8,
    variance: 0.15,
  },
  tier2: {
    // Good performers (40%)
    roasRange: [2.0, 3.5],
    ctrRange: [2.0, 3.0],
    trendDirection: 'up' as const,
    trendStrength: 0.3,
    variance: 0.2,
  },
  tier3: {
    // Average performers (20%)
    roasRange: [1.0, 2.0],
    ctrRange: [1.0, 2.0],
    trendDirection: 'flat' as const,
    trendStrength: 0.0,
    variance: 0.25,
  },
  tier4: {
    // Underperformers (10%)
    roasRange: [0.5, 1.0],
    ctrRange: [0.5, 1.0],
    trendDirection: 'down' as const,
    trendStrength: -0.4,
    variance: 0.3,
  },
} as const

/**
 * Platform-specific performance modifiers
 * Reflects real-world platform characteristics
 */
export const PLATFORM_MODIFIERS = {
  facebook: { roasMultiplier: 1.0, ctrMultiplier: 1.0, costMultiplier: 1.0 },
  google_display: {
    roasMultiplier: 0.6,
    ctrMultiplier: 0.4,
    costMultiplier: 0.8,
  },
  google_search: {
    roasMultiplier: 1.5,
    ctrMultiplier: 1.8,
    costMultiplier: 1.5,
  },
  performance_max: {
    roasMultiplier: 1.8,
    ctrMultiplier: 1.4,
    costMultiplier: 1.2,
  },
  tiktok: { roasMultiplier: 0.8, ctrMultiplier: 1.2, costMultiplier: 0.7 },
  line: { roasMultiplier: 0.9, ctrMultiplier: 1.1, costMultiplier: 0.9 },
} as const
